<script setup>
// WEXHUB: selector de alcance de una respuesta predefinida, compartido entre
// el alta y la edicion para que ambas pantallas se comporten igual.
import { computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  visibility: {
    type: String,
    default: 'account',
  },
  teamIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:visibility', 'update:teamIds']);

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');

onMounted(() => {
  if (!teams.value.length) store.dispatch('teams/get');
});

const SCOPES = ['user', 'team', 'account'];

const scopeOptions = computed(() =>
  SCOPES.map(value => ({
    value,
    label: t(`CANNED_MGMT.SCOPE.OPTIONS.${value.toUpperCase()}.LABEL`),
    description: t(`CANNED_MGMT.SCOPE.OPTIONS.${value.toUpperCase()}.DESC`),
  }))
);

const selectScope = value => {
  emit('update:visibility', value);
  // Los equipos elegidos solo tienen sentido en el alcance por equipo.
  if (value !== 'team') emit('update:teamIds', []);
};

const toggleTeam = teamId => {
  const selected = props.teamIds.includes(teamId)
    ? props.teamIds.filter(id => id !== teamId)
    : [...props.teamIds, teamId];
  emit('update:teamIds', selected);
};
</script>

<template>
  <div class="w-full">
    <label>{{ $t('CANNED_MGMT.SCOPE.LABEL') }}</label>
    <div class="flex flex-col gap-2 mb-2">
      <button
        v-for="option in scopeOptions"
        :key="option.value"
        type="button"
        class="flex gap-3 items-start p-3 text-left rounded-lg outline outline-1 -outline-offset-1 transition-colors"
        :class="
          visibility === option.value
            ? 'outline-n-brand bg-n-alpha-2'
            : 'outline-n-weak hover:outline-n-slate-6'
        "
        @click="selectScope(option.value)"
      >
        <span
          class="mt-1 w-4 h-4 rounded-full border-2 shrink-0"
          :class="
            visibility === option.value
              ? 'border-n-brand bg-n-brand'
              : 'border-n-slate-8'
          "
        />
        <span class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ option.label }}
          </span>
          <span class="text-xs text-n-slate-11">{{ option.description }}</span>
        </span>
      </button>
    </div>

    <div v-if="visibility === 'team'" class="mb-2">
      <label>{{ $t('CANNED_MGMT.SCOPE.TEAMS_LABEL') }}</label>
      <p v-if="!teams.length" class="text-sm text-n-slate-11">
        {{ $t('CANNED_MGMT.SCOPE.NO_TEAMS') }}
      </p>
      <div v-else class="flex flex-wrap gap-2">
        <button
          v-for="team in teams"
          :key="team.id"
          type="button"
          class="flex gap-1.5 items-center px-3 py-1.5 text-sm rounded-full outline outline-1 -outline-offset-1 transition-colors"
          :class="
            teamIds.includes(team.id)
              ? 'outline-n-brand bg-n-brand text-white'
              : 'outline-n-strong bg-n-alpha-1 text-n-slate-12 hover:outline-n-slate-6 hover:bg-n-alpha-2'
          "
          @click="toggleTeam(team.id)"
        >
          <Icon
            v-if="teamIds.includes(team.id)"
            icon="i-lucide-check"
            class="size-3.5 shrink-0"
          />
          {{ team.name }}
        </button>
      </div>
    </div>
  </div>
</template>
